import { supabase } from '../lib/supabase';
import { fetchCruiseSettings } from '../hooks/useCruiseSettings';

// Shared whole-cruise online-booking closure check for every booking-creation flow.
//
// Resolves the cruise NAME → cruise_code via cruise_settings (cached, same source
// the Seating Sheet uses), then looks for a row in online_booking_closures with
// that cruise_code, that DD-MM-YYYY date, and deck IS NULL — i.e. the WHOLE cruise
// is closed that day. Deck-only closures (deck = a name) do NOT block creation
// here: these flows pick a cruise + date but not a table/deck, so the booking can
// still land on an open deck, and the Seating Sheet enforces deck-level blocking
// at table placement.
//
// Returns the closure row { cruise_code, date, reason } when closed, else null.
export async function getWholeCruiseClosure(cruiseName, displayDate) {
  if (!cruiseName || !displayDate) return null;
  const settings = await fetchCruiseSettings();
  const code = settings.find(s => s.cruise_name === cruiseName)?.cruise_code;
  if (!code) return null;                       // unknown / inactive cruise → don't block
  const { data, error } = await supabase
    .from('online_booking_closures')
    .select('cruise_code, date, reason')
    .eq('cruise_code', code)
    .eq('date', displayDate)                    // dates are DD-MM-YYYY text → direct match
    .is('deck', null)                           // NULL deck = whole cruise closed
    .limit(1);
  if (error || !data || !data.length) return null;
  return data[0];
}

// Full closure picture for a cruise + date — used by read-only displays (e.g. the
// Dashboard capacity cards) that need BOTH the whole-cruise case and deck closures.
// Resolves cruise_code via cruise_settings and matches the DD-MM-YYYY date directly,
// same as getWholeCruiseClosure. Read-only.
//
// Returns { whole, decks } where:
//   whole = the deck=NULL closure row { cruise_code, date, reason } or null
//   decks = a map of { [deckName]: reason } for each deck-specific closure
export async function getCruiseClosures(cruiseName, displayDate) {
  const empty = { whole: null, decks: {} };
  if (!cruiseName || !displayDate) return empty;
  const settings = await fetchCruiseSettings();
  const code = settings.find(s => s.cruise_name === cruiseName)?.cruise_code;
  if (!code) return empty;
  const { data, error } = await supabase
    .from('online_booking_closures')
    .select('cruise_code, date, deck, reason')
    .eq('cruise_code', code)
    .eq('date', displayDate);                   // dates are DD-MM-YYYY text → direct match
  if (error || !data) return empty;
  const result = { whole: null, decks: {} };
  data.forEach(row => {
    if (!row.deck) { if (!result.whole) result.whole = row; }   // NULL deck = whole cruise
    else { result.decks[row.deck] = row.reason || ''; }
  });
  return result;
}

// Standard block message shared by every entry point.
// "This cruise is closed on [date] — [reason]."
export function closureMessage(closure, displayDate) {
  const reason = closure && closure.reason ? ` — ${closure.reason}` : '';
  return `This cruise is closed on ${displayDate}${reason}.`;
}
